/**
 * One `users/{uid}` listener for the whole tab.
 *
 * Both gates on this origin need the same two facts from that document —
 * whether Terms were accepted and where the recovery email stands — and the
 * portal used to get them as a side effect of the billing hook, which dragged
 * a `getBillingSubscription` callable into every boot that never renders a
 * price. The document is the gate's data; billing is a screen's data.
 *
 * Readiness is never allowed to hang: a refused or dropped listener resolves
 * with safe defaults, so a blocked read routes the visitor (to Terms, in the
 * worst case) instead of holding a loader forever.
 */
import type { DocumentData } from 'firebase/firestore';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../config/firebase';

export type RecoveryEmailState = {
  recoveryEmail: string | null;
  recoveryEmailVerified: boolean;
  termsAccepted: boolean;
};

export const EMPTY_RECOVERY: RecoveryEmailState = {
  recoveryEmail: null,
  recoveryEmailVerified: false,
  termsAccepted: false,
};

const READY_TIMEOUT_MS = 8000;

type Listener = (state: RecoveryEmailState, ready: boolean) => void;

type Entry = {
  uid: string;
  state: RecoveryEmailState;
  ready: boolean;
  listeners: Set<Listener>;
  stop: (() => void) | null;
  timer: ReturnType<typeof setTimeout> | null;
};

/** One signed-in user per tab, so one entry is enough. */
let entry: Entry | null = null;

function toRecovery(data: DocumentData | undefined): RecoveryEmailState {
  const d = data || {};
  return {
    recoveryEmail: typeof d.recoveryEmail === 'string' ? d.recoveryEmail : null,
    recoveryEmailVerified: d.recoveryEmailVerified === true,
    termsAccepted: d.termsAcceptedAt != null,
  };
}

function emit(e: Entry): void {
  for (const listener of e.listeners) listener(e.state, e.ready);
}

function clearTimer(e: Entry): void {
  if (e.timer) {
    clearTimeout(e.timer);
    e.timer = null;
  }
}

function settle(e: Entry, state: RecoveryEmailState): void {
  clearTimer(e);
  e.state = state;
  e.ready = true;
  emit(e);
}

function entryFor(uid: string): Entry {
  if (entry && entry.uid === uid) return entry;
  if (entry) {
    entry.stop?.();
    clearTimer(entry);
  }
  entry = {
    uid,
    state: EMPTY_RECOVERY,
    ready: false,
    listeners: new Set(),
    stop: null,
    timer: null,
  };
  return entry;
}

function start(e: Entry): void {
  if (e.stop) return;
  e.stop = onSnapshot(
    doc(getFirebaseDb(), 'users', e.uid),
    (snap) => settle(e, toRecovery(snap.data())),
    () => settle(e, e.ready ? e.state : EMPTY_RECOVERY),
  );
  if (!e.ready && !e.timer) {
    e.timer = setTimeout(() => settle(e, e.state), READY_TIMEOUT_MS);
  }
}

export function cachedRecovery(uid: string): {
  recovery: RecoveryEmailState;
  ready: boolean;
} {
  const e = entry && entry.uid === uid ? entry : null;
  return { recovery: e?.state ?? EMPTY_RECOVERY, ready: !!e?.ready };
}

/**
 * The last listener leaving closes the snapshot but keeps what it read, so
 * walking back into a gated surface replays the cached answer immediately
 * rather than flashing a loader while the document is re-read.
 */
export function subscribeUserDoc(uid: string, listener: Listener): () => void {
  const e = entryFor(uid);
  e.listeners.add(listener);
  start(e);
  listener(e.state, e.ready);

  return () => {
    e.listeners.delete(listener);
    if (e.listeners.size === 0) {
      e.stop?.();
      e.stop = null;
      clearTimer(e);
    }
  };
}

export function resetUserDocStore(): void {
  if (!entry) return;
  entry.stop?.();
  clearTimer(entry);
  entry = null;
}
